/*----------------------------------
- LANDING: ACCESS OFFER COPY (SSOT)
----------------------------------*/

/**
 * @docs docs/features/plan-tiering
 * @adr  ADR-0083
 * @rule The website states the paid-only access model from these lines only: one plan, its price
 *       from `PUBLIC_PLAN_FACTS`, the refund from `REFUND_GUARANTEE_DAYS`, and the public previews
 *       that need no account. No line here may promise a free plan, a trial or credits.
 */

/*----------------------------------
- DEPENDENCES
----------------------------------*/

import { ANALYSES_RESET_PHRASE } from '@uniquedomains/components/pricing/catalogs/analysesReset';
import { PUBLIC_EDITION_OPEN_NAMES } from '@uniquedomains/components/contracts/editions/public';
import { PUBLIC_LOOKUPS_PER_TOOL_PER_DAY } from '@uniquedomains/components/contracts/lookup/limits';
import { DATABASE_GUEST_RESULTS_LIMIT } from '@uniquedomains/components/contracts/search/searchAccessCatalog';
import { PUBLIC_PLAN_FACTS } from '@uniquedomains/components/pricing/catalogs/pricingPlans';
import { REFUND_GUARANTEE_DAYS } from '@uniquedomains/components/pricing/catalogs/refundPromise';

/*----------------------------------
- CATALOG (SSOT)
----------------------------------*/

/**
 * The site's signup CTA (header, home, feature and solution pages). It replaced "Start Free" on
 * 2026-10-17: a signup now opens the two-screen brief and the decision screen, never a free
 * workspace, so the label names the first thing the visitor does.
 */
export const START_WITH_BRIEF_LABEL = 'Start with my brief';

/**
 * The header pill's call to action since the frame moved onto the design system (batch 7, decision 2A, Gaetan
 * 2026-10-26): "brief" is never a noun in website copy, and the header alone reads the short form. The final push
 * lands after the paid-only read, so the wording change rides with it; if that date moves before the read ends,
 * this one line falls back to `START_WITH_BRIEF_LABEL`.
 */
export const HEADER_CTA_LABEL = 'Start my search';

/** Every other signup call to action of the rebuilt pages (the closing calls). */
export const START_WITH_SEARCH_LABEL = 'Start with my search';

/**
 * When the analysis quota restarts (decision L12 of the paid-funnel plan): the quota counts
 * analyses granted in the current UTC calendar month, never from the billing day.
 */
export const ANALYSES_RESET_SENTENCE = `The count ${ANALYSES_RESET_PHRASE}.`;

/** The terms under a plan card, in one line. */
export const FULL_ACCESS_TERMS_LINE = `$${PUBLIC_PLAN_FACTS.member.price} a month, cancel anytime, money back within ${REFUND_GUARANTEE_DAYS} days.`;

/**
 * What a visitor can read without an account, for the "is there a free way to try it"
 * answers. Each preview is a bounded public surface (AGENTS.md, Minimum Data Exposure), and
 * each count is read from the constant that caps it.
 */
const LOOKUPS_A_DAY = PUBLIC_LOOKUPS_PER_TOOL_PER_DAY === 1 ? 'one name a day' : `${PUBLIC_LOOKUPS_PER_TOOL_PER_DAY} names a day`;

export const PUBLIC_PREVIEW_SENTENCE = `The public domain lists show the first ${DATABASE_GUEST_RESULTS_LIMIT} names of a search, each daily edition opens ${PUBLIC_EDITION_OPEN_NAMES} names a day, and the WHOIS and availability tools check ${LOOKUPS_A_DAY}, all without an account.`;

/** The answer to "is there a free way to try it", shared by the feature pages. */
export const FREE_WAY_TO_TRY_ANSWER = `${PUBLIC_PREVIEW_SENTENCE} Your search and its matches come with Full Access: ${FULL_ACCESS_TERMS_LINE}`;
